import React, { FC } from "react";
import { Grid, Stack, Typography } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import ReactMarkdown from "react-markdown";

import { NAMED_COLORS } from "@src/utils";
import StackContent from "@components/StackContent";
import { CardData } from "@src/types";
import BaseLink from "@components/Link";
import { Colors } from "@src/gatsby-theme-material-ui-top-layout/colors";

interface Props {
  title: string;
  description?: string;
  cards: Array<string | CardData>;
  backgroundColor?: string;
  cardColor?: string;
}

interface CardProps {
  title?: string;
  description?: string;
  ctaUrl?: string;
  ctaLabel?: string;
  cardColor?: string;
}

const Card: FC<CardProps> = ({
  title,
  description,
  ctaUrl,
  ctaLabel,
  cardColor,
}) => {
  const padding = cardColor ? 3 : 0; // no padding if cards have no background
  return (
    <Grid item>
      <Stack
        spacing={2}
        p={padding}
        justifyContent="space-between"
        sx={{
          height: "100%",
          backgroundColor: cardColor || "unset",
          borderRadius: "15px",
        }}
      >
        <Stack spacing={2}>
          {title && (
            <Typography
              variant="h6"
              component="div"
              sx={{ color: Colors.TextPrimary, margin: "-1em 0!important" }}
            >
              <ReactMarkdown children={title} />
            </Typography>
          )}
          {description && (
            <Typography
              variant="body2"
              component="div"
              sx={{ color: Colors.TextPrimary }}
            >
              <ReactMarkdown children={description} />
            </Typography>
          )}
        </Stack>
        {ctaUrl && (
          <Stack direction="row">
            <BaseLink url={ctaUrl}>{ctaLabel}</BaseLink>
          </Stack>
        )}
      </Stack>
    </Grid>
  );
};

const CardList: FC<Props> = ({
  title,
  description,
  cards,
  backgroundColor,
  cardColor,
}) => {
  const bgHex: string =
    backgroundColor && backgroundColor in NAMED_COLORS
      ? NAMED_COLORS[backgroundColor]
      : "";
  const cardHex: string =
    cardColor && cardColor in NAMED_COLORS ? NAMED_COLORS[cardColor] : "";

  const cardCount = cards.length;
  const minColumns = cardCount < 2 ? 2 : cardCount; // minimum of 2 columns
  const mdFixColumns =
    cardCount >= minColumns && cardCount % 3 === 1
      ? "repeat(2, 1fr)"
      : "repeat(3, 1fr)";
  const lgFixColumns =
    cardCount >= minColumns && cardCount % 4 === 1
      ? "repeat(3, 1fr)"
      : "repeat(4, 1fr)";

  const smCols =
    cards.length < 2 || cardCount % 2 === 1 ? "1fr" : "repeat(2, 1fr)";
  const mdCols = cards.length < 3 ? `repeat(${minColumns}, 1fr)` : mdFixColumns;
  const lgCols = cards.length < 4 ? `repeat(${minColumns}, 1fr)` : lgFixColumns;

  const isMobile = useMediaQuery("(max-width:480px)");

  return (
    <StackContent>
      {(title || description) && (
        <Stack spacing={2} mb={3}>
          {title && <Typography variant="h2">{title}</Typography>}
          {description && (
            <Typography variant="h6" sx={{ color: Colors.TextPrimary }}>
              {description}
            </Typography>
          )}
        </Stack>
      )}
      <Stack alignItems="center">
        <Grid
          container
          gap={2.5}
          sx={{
            backgroundColor: bgHex || "none",
            display: "grid",
            gridTemplateColumns: {
              xs: isMobile ? "minmax(100px, 1fr)" : smCols,
              sm: mdCols,
              md: lgCols,
              lg: lgCols,
            },
            gridTemplateRows: "auto",
          }}
        >
          {cards.map((card, idx) => {
            if (typeof card === "string") {
              return (
                <Card key={`card-${idx}`} title={card} cardColor={cardHex} />
              );
            } else {
              return (
                <Card
                  key={`card-${idx}`}
                  title={card.title}
                  description={card.description}
                  ctaUrl={card.ctaUrl}
                  ctaLabel={card.ctaLabel}
                  cardColor={cardHex}
                />
              );
            }
          })}
        </Grid>
      </Stack>
    </StackContent>
  );
};

export default CardList;
